import type { ChildAppConfig } from '@shared/app-config'
import type { ProxyApiInstance } from '@/proxy/shared/types'

import { loadGlobalAutoloadWidgets } from '@shared/api/web-components/global-autoload'
// Dev proxy doesn't use setAppConfig — it works with ChildAppConfig directly
import { GLOBAL_API_PROVIDER, GLOBAL_CONFIG_VAR } from '@shared/constants/global'
import { createCssVariables, createStyle } from '@shared/util/createStyle'
import { applyThemeMode } from '@shared/util/theme-mode'

import { iframeCss, markdownCss, primeVueCss, themeConfigCss } from '@/proxy/dev/css-inline'
import { createDevEvents } from '@/proxy/dev/events'

import { createDevHostAPI } from '@/proxy/dev/host-stubs'
import { resolveDevConfig } from '@/proxy/dev/resolve-dev'
import { createApi } from '@/proxy/shared/api'
import { createForm } from '@/proxy/shared/form'
import { installHistoryGuard } from '@/proxy/shared/history-polyfill'
import { createIframeAPI } from '@/proxy/shared/host'

import { createHtmlAPI } from '@/proxy/shared/html'
import { addIcons } from '@/proxy/shared/icons'
import { createLoggerAPI } from '@/proxy/shared/log'
import { installPreventLinkClicks, setPreventClicksHost } from '@/proxy/shared/prevent-clicks'
import { createSanitizeAPI } from '@/proxy/shared/sanitize'
import { createStateAPI } from '@/proxy/shared/state'
import { createTagAllowlistTracker } from '@/proxy/shared/tag-allowlist-tracker'

import tailwindConfig from '@/proxy/shared/talwind'
import { loadByTagName, loadWebComponent, parseDomForLoaders } from '@/proxy/shared/web-component-loader'
import { createWsAPI } from '@/proxy/shared/ws'
import '@/proxy/components/w-iframe'
import '@/proxy/components/w-artifact'
import '../../npm/@wippy-fe--loading/src/index'

declare global {
  interface Window {
    getWippyApi: () => Promise<ProxyApiInstance>
    /**
     * @deprecated
     */
    initWippyApi: () => Promise<ProxyApiInstance>
    $W: {
      config: () => Promise<ChildAppConfig>
      instance: () => Promise<ProxyApiInstance>
      api: () => Promise<ProxyApiInstance['api']>
      /** @deprecated Use `$W.api` instead */
      form: () => Promise<ProxyApiInstance['api']>
      host: () => Promise<ProxyApiInstance['host']>
      /** @deprecated Use `$W.host` instead */
      iframe: () => Promise<ProxyApiInstance['host']>
      on: () => Promise<ProxyApiInstance['on']>
      logger: () => Promise<ProxyApiInstance['logger']>
      state: () => Promise<ProxyApiInstance['state']>
      ws: () => Promise<ProxyApiInstance['ws']>
      loadWebComponent: () => Promise<ProxyApiInstance['loadWebComponent']>
      loadByTagName: () => Promise<ProxyApiInstance['loadByTagName']>
      sanitize: () => Promise<ProxyApiInstance['sanitize']>
      html: () => Promise<ProxyApiInstance['html']>
    }
    tailwind?: {
      config: Partial<typeof tailwindConfig>
    }
  }
}

let instance: ProxyApiInstance | null = null
let instancePromise: Promise<ProxyApiInstance> | null = null

// Per-iframe allowlist tracker — module-scoped so the same instance
// reaches `loadByTagName` / `loadWebComponent` / `parseDomForLoaders`
// /`loadGlobalAutoloadWidgets` AND backs `$W.sanitize`.
const tagAllowlist = createTagAllowlistTracker()

// Install history guard early (stubs history methods for srcdoc safety)
installHistoryGuard()

async function createInstance() {
  const { appConfig, proxyConfig, overlay } = await resolveDevConfig()

  const {
    css,
    tailwindConfig: useTailwindConfig,
    resizeObserver: useResizeObserver,
    preventLinkClicks,
    iconifyIcons,
  } = proxyConfig.injections

  // Dev events — exposes emitter for host stubs
  const { on, emitter } = createDevEvents()

  // Wire overlay refresh button → fire @visibility(true)
  overlay.setOnRefresh(() => {
    emitter.emit('@visibility', true)
  })

  // Set global config for getEnv() — cast is safe, ChildAppConfig has env at top level
  window[GLOBAL_CONFIG_VAR] = appConfig

  // Order: host → api (api's 401 interceptor calls host.handleError).
  const host = createDevHostAPI(appConfig, emitter, overlay)
  const iframe = createIframeAPI(host)
  const api = createApi(appConfig, host)
  const form = createForm(appConfig, api) as ProxyApiInstance['form']
  const html = createHtmlAPI()

  const config = {
    ...appConfig,
    get auth() {
      console.warn('Attention: Dont use config.auth directly, unless you really need to, use `api` instead to wrap API calls')
      return appConfig.auth
    },
  }

  const loadWebComponentFn = (componentId: string, tagName?: string) => {
    return loadWebComponent(api, componentId, tagName, tagAllowlist)
  }

  const loadByTagNameFn = (tag: string, options?: { timeoutMs?: number }) => {
    return loadByTagName(api, tag, options, tagAllowlist)
  }

  const sanitize = createSanitizeAPI(tagAllowlist.get)

  const logger = createLoggerAPI(appConfig)

  const proxyInstance: ProxyApiInstance = {
    config,
    get iframe() {
      console.warn('Attention: Dont use instance.iframe directly, use instance.host instead')
      return iframe
    },
    host,
    api,
    get form() {
      console.warn('Attention: Dont use instance.form directly, use instance.api instead')
      return form
    },
    on: on as ProxyApiInstance['on'],
    logger,
    state: createStateAPI(),
    ws: createWsAPI(),
    loadWebComponent: loadWebComponentFn,
    loadByTagName: loadByTagNameFn,
    sanitize,
    html,
  }

  instance = proxyInstance

  // Add icons if enabled
  if (iconifyIcons) {
    const IconifyIcon = window.customElements.get('iconify-icon')
    if (!IconifyIcon) {
      try {
        const mod = await import('iconify-icon')
        if (mod.addCollection)
          addIcons(mod.addCollection, config)
      }
      catch {
        console.warn('Failed to auto-load iconify-icon')
      }
    }
    else {
      addIcons((IconifyIcon as any).addCollection.bind(IconifyIcon), config)
    }
  }

  window[GLOBAL_API_PROVIDER] = proxyInstance
  window[GLOBAL_CONFIG_VAR] = config

  // Apply Tailwind config if enabled
  if (useTailwindConfig && window.tailwind) {
    const { content, ...rest } = tailwindConfig
    window.tailwind.config = rest
  }

  // Apply CSS injections — inline (no external URLs)
  if (css.themeConfig)
    createStyle(themeConfigCss, 'theme-config')
  if (css.iframe)
    createStyle(iframeCss, 'iframe')
  if (css.primevue)
    createStyle(primeVueCss, 'primevue')
  if (css.markdown)
    createStyle(markdownCss, 'markdown')

  // Apply custom CSS and variables after base styles (pinned to end by MutationObserver)
  if (css.customCss && appConfig.theming?.global?.customCSS)
    createStyle(appConfig.theming.global.customCSS, 'custom-css')
  if (css.customVariables && appConfig.theming?.global?.cssVariables)
    createCssVariables(appConfig.theming.global.cssVariables)

  // Forced theme mode for the standalone dev proxy (mirrors entry.iframe.ts).
  applyThemeMode(appConfig.themeMode)
  on('@theme', mode => applyThemeMode(mode))

  // Prevent link clicks if enabled
  setPreventClicksHost(host)
  if (preventLinkClicks)
    installPreventLinkClicks()

  // Resize observer — reports to overlay instead of parent
  if (useResizeObserver) {
    const resizeObserver = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect
      overlay.updateSize(width, height)
    })
    if (document.body)
      resizeObserver.observe(document.body)
    else
      addEventListener('DOMContentLoaded', () => resizeObserver.observe(document.body))
  }

  // Title observer — reports to overlay instead of parent
  const setupTitleObserver = () => {
    let lastTitle = ''
    const sendTitle = () => {
      if (document.title && document.title !== lastTitle) {
        lastTitle = document.title
        overlay.updateTitle(document.title)
      }
    }
    sendTitle()
    const titleObserver = new MutationObserver(() => sendTitle())
    titleObserver.observe(document.head, { childList: true, subtree: true, characterData: true })
  }

  if (document.head)
    setupTitleObserver()
  else
    addEventListener('DOMContentLoaded', setupTitleObserver)

  // eslint-disable-next-line no-console
  console.log('Checking custom elements')
  await Promise.all([
    loadGlobalAutoloadWidgets(api, tagAllowlist),
    parseDomForLoaders(api, document, tagAllowlist),
  ])
  // eslint-disable-next-line no-console
  console.log('[wippy-dev] Dev Proxy API Ready', appConfig.context?.resourceId, appConfig)

  return proxyInstance
}

function initPromise(): Promise<ProxyApiInstance> {
  if (instancePromise)
    return instancePromise

  instancePromise = (async () => {
    if (instance)
      return instance
    return await createInstance()
  })()

  return instancePromise!
}

window.initWippyApi = initPromise
window.getWippyApi = initPromise

// Global error-capture parity with `entry.iframe.ts`. In the hosted runtime
// these get forwarded to the host's logger via `captureException`; in dev,
// the dev-proxy's logger stub console.logs them so devs see uncaught errors
// in the same place the real host would surface them. Without this, a
// thrown error in a child app under dev-proxy was invisible unless the dev
// happened to have the browser console open.
window.onerror = (_message, _source, _lineno, _colno, error) => {
  initPromise().then(inst => inst.logger.captureException(error ?? _message, {
    source: _source,
    lineno: _lineno,
    colno: _colno,
    unhandled: true,
  })).catch(() => {})
}
window.addEventListener('unhandledrejection', (event) => {
  initPromise().then(inst => inst.logger.captureException(event.reason, {
    unhandled: true,
    type: 'unhandledrejection',
  })).catch(() => {})
})

window.$W = {
  config: async () => (await initPromise()).config,
  instance: async () => await initPromise(),
  api: async () => (await initPromise()).api,
  form: async () => {
    console.warn('$W.form is deprecated, use $W.api instead')
    return (await initPromise()).api
  },
  iframe: async () => {
    console.warn('$W.iframe is deprecated, use $W.host instead')
    return (await initPromise()).host
  },
  host: async () => (await initPromise()).host,
  on: async () => (await initPromise()).on,
  logger: async () => (await initPromise()).logger,
  state: async () => (await initPromise()).state,
  ws: async () => (await initPromise()).ws,
  loadWebComponent: async () => (await initPromise()).loadWebComponent,
  loadByTagName: async () => (await initPromise()).loadByTagName,
  sanitize: async () => (await initPromise()).sanitize,
  html: async () => (await initPromise()).html,
}

window.getWippyApi().then(() => {
  // eslint-disable-next-line no-console
  console.log('[wippy-dev] Dev Proxy Initialized', {
    BUILD_TIME: import.meta.env.APP_BUILD_TIME,
    BUILD_VERSION: import.meta.env.APP_BUILD_VERSION,
  })
}).catch((error) => {
  console.error('[wippy-dev] Error initializing Dev Proxy', error)
})
